import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import axios, {
  uploadTaskAttachments,
  listTaskAttachments,
  deleteTaskAttachment,
  listTaskComments,
  addTaskComment,
  deleteTaskComment,
  updateTaskComment,
  markTaskAsViewed,
} from "../../api/axiosConfig";
import { getSocket } from "../../api/socketService";
import {
  FaSave,
  FaTrash,
  FaExpandAlt,
  FaTimes,
  FaPaperclip,
  FaCommentDots,
} from "react-icons/fa";
import { getInitials, getAvatarColor } from "../../utils/avatar";

const PRIO_TEXT = ["Low", "Medium", "High", "Highest"];

/* ---------- Helpers ---------- */
function stripHtml(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html || "";
  return tmp.textContent || tmp.innerText || "";
}
/* Aktuellen User aus JWT (localStorage) ermitteln */
function getCurrentUserFromToken() {
  try {
    const t = localStorage.getItem("token");
    if (!t) return { id: null, name: null, email: null };
    const [, payload] = t.split(".");
    if (!payload) return { id: null, name: null, email: null };
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    // Versuche möglichst viele gängige Felder
    const id    = json.id ?? json.userId ?? json.uid ?? json.user_id ?? (json.sub ?? null);
    const name  = json.name ?? json.username ?? null;
    const email = json.email ?? json.mail ?? null;
    return { id, name, email };
  } catch {
    return { id: null, name: null, email: null };
  }
}

/* ---------- Lightweight RichTextEditor (ohne deps) ---------- */
function RichTextEditor({
  value,
  onChange,
  placeholder = "Beschreibung …",
  mode = "full", // "full" | "comment"
  onUploadImages, // async (File[]) => string[] (URLs)
  mentionUsers = [],
  mentionExcludeIds = [],
  onMentionAdd,
}) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const dropdownRef = useRef(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
  };

  const onPaste = (e) => {
    // plain paste (kein fremdes Styling)
    if (!e.clipboardData) return;
    const items = e.clipboardData.items;
    const imgItems = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f && f.type.startsWith("image/")) imgItems.push(f);
      }
    }
    if (imgItems.length && onUploadImages) {
      e.preventDefault();
      onUploadImages(imgItems).then((urls) => {
        urls.forEach((u) => exec("insertImage", u));
      });
      return;
    }
    // sonst: Text als plain einfügen
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    ref.current?.classList.add("drag-over");
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    ref.current?.classList.remove("drag-over");
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    ref.current?.classList.remove("drag-over");
    
    if (!e.dataTransfer) return;
    const files = Array.from(e.dataTransfer.files || []);
    const imgFiles = files.filter((f) => f.type.startsWith("image/"));
    
    if (imgFiles.length && onUploadImages) {
      onUploadImages(imgFiles).then((urls) => {
        urls.forEach((u) => exec("insertImage", u));
      });
    }
  };

  const wantImage = async () => {
    if (!onUploadImages) return;
    fileRef.current?.click();
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !onUploadImages) return;
    const urls = await onUploadImages(files);
    urls.forEach((u) => exec("insertImage", u));
  };

  const getTextBeforeCaret = (sel) => {
    if (!ref.current) return "";
    const range = sel.getRangeAt(0).cloneRange();
    const pre = range.cloneRange();
    pre.selectNodeContents(ref.current);
    pre.setEnd(range.endContainer, range.endOffset);
    return pre.toString();
  };

  const detectMention = () => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !ref.current) {
      setMentionQuery("");
      setMentionSelectedIndex(0);
      return;
    }
    if (!ref.current.contains(sel.anchorNode)) {
      setMentionQuery("");
      setMentionSelectedIndex(0);
      return;
    }
    const text = getTextBeforeCaret(sel);
    // Match @ at word boundary, followed by 0-30 word chars
    // Handles: "text @", "text @a", "text @abc", "text @ab", etc
    const match = text.match(/(^|\s)@([\w._-]*)$/);
    if (match && match[2] !== undefined) {
      setMentionQuery(match[2]);
      setMentionSelectedIndex(0);
      // Berechne Position des Dropdowns relativ zum Editor
      const rect = ref.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    } else {
      setMentionQuery("");
      setMentionSelectedIndex(0);
    }
  };

  const replaceActiveMentionToken = (label) => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !ref.current) return;
    if (!ref.current.contains(sel.anchorNode)) return;

    let range = sel.getRangeAt(0);
    let { endContainer, endOffset } = range;
    if (endContainer.nodeType !== Node.TEXT_NODE) {
      const textNode = document.createTextNode("");
      range.insertNode(textNode);
      range = document.createRange();
      range.setStart(textNode, 0);
      range.setEnd(textNode, 0);
      endContainer = textNode;
      endOffset = 0;
    }

    const before = (endContainer.textContent || "").slice(0, endOffset);
    // Match @word pattern - ensure it's a proper mention, not just @@
    const match = before.match(/\s@([\w._-]{0,30})$|^@([\w._-]{0,30})$/);
    if (!match) return;
    
    const startOffset = before.length - (before.match(/\s@([\w._-]{0,30})$/) ? before.match(/\s@([\w._-]{0,30})$/)[0].length : before.match(/^@([\w._-]{0,30})$/)[0].length);

    const newRange = document.createRange();
    newRange.setStart(endContainer, startOffset);
    newRange.setEnd(endContainer, endOffset);
    newRange.deleteContents();

    const node = document.createTextNode(`@${label} `);
    newRange.insertNode(node);
    newRange.setStartAfter(node);
    newRange.setEndAfter(node);
    sel.removeAllRanges();
    sel.addRange(newRange);

    onChange?.(ref.current?.innerHTML || "");
    setMentionQuery("");
    setMentionSelectedIndex(0);
  };

  const mentionSuggestions = useMemo(() => {
    const q = mentionQuery.trim().toLowerCase();
    if (q === "") return []; // No suggestions if query is empty
    const filtered = mentionUsers
      .filter((u) => !mentionExcludeIds.includes(u.id))
      .filter((u) => {
        const name = (u.name || "").toLowerCase();
        const mail = (u.email || "").toLowerCase();
        return name.includes(q) || mail.includes(q) || String(u.id).includes(q);
      })
      .slice(0, 5);
    return filtered;
  }, [mentionQuery, mentionUsers, mentionExcludeIds]);

  const toolbar = (
    <div className="rte-toolbar">
      <button type="button" onClick={() => exec("bold")}>
        B
      </button>
      <button type="button" onClick={() => exec("italic")}>
        <i>I</i>
      </button>
      <button type="button" onClick={() => exec("underline")}>
        <u>U</u>
      </button>
      <span className="sep" />
      {mode === "full" && (
        <>
          <button type="button" onClick={() => exec("formatBlock", "h2")}>
            H2
          </button>
          <button type="button" onClick={() => exec("formatBlock", "h3")}>
            H3
          </button>
          <span className="sep" />
          <button type="button" onClick={() => exec("insertUnorderedList")}>
            • Liste
          </button>
          <button type="button" onClick={() => exec("insertOrderedList")}>
            1. Liste
          </button>
          <span className="sep" />
        </>
      )}
      <button
        type="button"
        onClick={() => {
          let url = prompt("Link-URL:");
          if (url) {
            // Wenn kein Protokoll angegeben ist → https:// hinzufügen
            if (!/^https?:\/\//i.test(url)) {
              url = "https://" + url;
            }
            exec("createLink", url);
          }
        }}
      >
        Link
      </button>
      {onUploadImages && (
        <>
          <button type="button" onClick={wantImage}>
            Bild
          </button>
          <input
            ref={fileRef}
            hidden
            type="file"
            accept="image/*"
            multiple
            onChange={onPickFiles}
          />
        </>
      )}
      <button type="button" onClick={() => exec("removeFormat")}>
        Format löschen
      </button>
    </div>
  );

  return (
    <div className="rte">
      {toolbar}
      <div
        ref={ref}
        className="rte-editor"
        contentEditable
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        onKeyUp={detectMention}
        onKeyDown={(e) => {
          if (!mentionQuery) return;
          const len = mentionSuggestions.length;
          if (!len) return;
          
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setMentionSelectedIndex((i) => (i + 1) % len);
            return;
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            setMentionSelectedIndex((i) => (i - 1 + len) % len);
            return;
          }
          if (e.key === "Enter") {
            e.preventDefault();
            const u = mentionSuggestions[mentionSelectedIndex];
            replaceActiveMentionToken(u.name || u.email || u.id);
            onMentionAdd?.(u);
            return;
          }
        }}
        onInput={() => {
          onChange?.(ref.current?.innerHTML || "");
          detectMention(); // Re-detect mentions on every input
        }}
        onPaste={onPaste}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        suppressContentEditableWarning
        style={{ minHeight: mode === "comment" ? 80 : 180 }}
      />
      {mentionQuery.length > 0 && mentionSuggestions.length > 0 && (
        <ul
          ref={dropdownRef}
          className="autocomplete-suggestions"
          style={{
            position: "fixed",
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            marginTop: 0,
            padding: "4px 0",
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            maxHeight: 220,
            overflowY: "auto",
            zIndex: 5000,
            boxShadow: "var(--shadow-md)",
          }}
        >
          {mentionSuggestions.map((u, idx) => (
            <li
              key={u.id}
              className={`autocomplete-suggestion ${idx === mentionSelectedIndex ? "selected" : ""}`}
              style={{
                padding: "8px 12px",
                fontSize: 14,
                lineHeight: 1.35,
                cursor: "pointer",
                background: idx === mentionSelectedIndex ? "var(--brand)" : "var(--surface)",
                color: idx === mentionSelectedIndex ? "#fff" : "var(--text)",
                listStyle: "none"
              }}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setMentionSelectedIndex(idx)}
              onClick={() => {
                replaceActiveMentionToken(u.name || u.email || u.id);
                onMentionAdd?.(u);
              }}
            >
              {u.name || u.email} ({u.email || u.name || u.id})
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------- Lightbox ---------- */
function Lightbox({ items, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  const it = items[index];
  if (!it) return null;

  return (
    <div className="lb_backdrop" onMouseDown={onClose}>
      <div className="lb_content" onMouseDown={(e) => e.stopPropagation()}>
        <button className="lb_close" onClick={onClose}>
          ×
        </button>
        <button className="lb_nav lb_prev" onClick={onPrev}>
          ‹
        </button>
        <button className="lb_nav lb_next" onClick={onNext}>
          ›
        </button>
        <img className="lb_img" src={it.url} alt={it.name} />
        <div className="lb_caption">
          <span title={it.name}>{it.name}</span>
        </div>
      </div>
    </div>
  );
}

/* ---------- Attachments-Panel (im Vollbild) ---------- */
function AttachmentsPanel({ taskId, externalItems = [] }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lbIndex, setLbIndex] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const dropZoneRef = useRef(null);

  const load = useCallback(async () => {
    const res = await listTaskAttachments(taskId);
    setItems(res || []);
  }, [taskId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFiles = useCallback(async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      await uploadTaskAttachments(taskId, Array.from(files));
      await load();
    } finally {
      setBusy(false);
    }
  }, [taskId, load]);

  // Handle Paste events globally when drop zone is visible
  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files = [];
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.kind === "file") {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length) {
        e.preventDefault();
        handleFiles(files);
      }
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [handleFiles]);

  async function onSelect(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      await uploadTaskAttachments(taskId, files);
      await load();
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  }

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    
    const files = e.dataTransfer?.files;
    if (files?.length) {
      handleFiles(files);
    }
  };

  async function onDelete(attId) {
    setBusy(true);
    try {
      await deleteTaskAttachment(taskId, attId);
      setItems((prev) => prev.filter((x) => x.id !== attId));
      setLbIndex(null);
    } finally {
      setBusy(false);
    }
  }

  // --- Externe Anhänge normalisieren (read-only) ---
  const normalizedExternal = useMemo(() => {
    const arr = Array.isArray(externalItems) ? externalItems : [];
    return arr
      .filter((x) => x && typeof x.url === "string")
      .map((x, i) => ({
        id: `ext-${i}`,
        url: x.url,
        name: x.filename || x.name || x.url,
        mime: x.mimetype || x.type || null,
        size: Number.isFinite(x.size) ? x.size : null,
        __external: true,
      }));
  }, [externalItems]);

  // --- Gemischte Liste: interne zuerst, dann externe ---
  const mixed = useMemo(() => [...items, ...normalizedExternal], [items, normalizedExternal]);

  const isImage = (it) => {
    if (it.mime && it.mime.startsWith?.("image/")) return true;
    // Fallback: Dateiendung prüfen
    const m = /\.([a-z0-9]+)(?:\?|#|$)/i.exec(it.url || "");
    const ext = (m?.[1] || "").toLowerCase();
    return ["png", "jpg", "jpeg", "webp", "gif", "bmp", "svg", "heic", "heif"].includes(ext);
  };

  const images = mixed.filter(isImage);

  return (
    <div className="attachments-section">
      <div className="attachments-header">
        <h4>Anhänge</h4>
        <label className={`btn btn-secondary btn-sm ${busy ? "disabled" : ""}`}>
          Datei(en) anhängen
          <input
            ref={fileRef}
            hidden
            type="file"
            multiple
            accept=".png,.jpg,.jpeg,.webp,.gif,.bmp,.svg,.heic,.heif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.odt,.ods,.txt,.rtf,.zip,.7z,.rar"
            onChange={onSelect}
          />
        </label>
      </div>

      {/* Drag-and-Drop Zone */}
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        ref={dropZoneRef}
        tabIndex="0"
        className={`dropzone ${dragOver ? "is-dragover" : ""}`}
      >
        <div className="dropzone-title">📁 Dateien ziehen oder klicken</div>
        <div className="dropzone-hint">
          Oder Strg+V zum Einfügen aus Zwischenablage
        </div>
      </div>

      {mixed.length === 0 ? (
        <p className="muted">Noch keine Anhänge.</p>
      ) : (
        <div className="attachment-grid">
          {mixed.map((a) => {
            const isImg = isImage(a);
            return (
              <div key={a.id} className="attachment-card">
                {isImg ? (
                  <button
                    className="thumb_btn"
                    onClick={() => setLbIndex(images.findIndex((i) => i.id === a.id))}
                    title="Ansehen"
                  >
                    <img src={a.url} alt={a.name} />
                  </button>
                ) : (
                  <a
                    className="file-pill"
                    href={a.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {a.name}
                  </a>
                )}
                <div className="attachment-meta">
                  <span className="name" title={a.name}>
                    {a.name}
                  </span>
                  {!a.__external && (
                    <button
                      className="del_btn"
                      onClick={() => onDelete(a.id)}
                      disabled={busy}
                    >
                      Löschen
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {lbIndex != null && images[lbIndex] && (
        <Lightbox
          items={images}
          index={lbIndex}
          onClose={() => setLbIndex(null)}
          onPrev={() => setLbIndex((i) => (i - 1 + images.length) % images.length)}
          onNext={() => setLbIndex((i) => (i + 1) % images.length)}
        />
      )}
    </div>
  );
}


/* ---------- TaskItem ---------- */
function TaskItem({
  task,
  boardId,
  boardUsers = [],
  onTaskUpdated,
  onTaskDeleted,
  onTaskMoved,
  availableBoards = [],
  dragHandleProps = {},
}) {
  // Inline-Edit (bestehend)
  const initialName = useMemo(
    () => boardUsers.find((u) => u.id === task.assignee)?.name || "",
    [boardUsers, task.assignee]
  );
  const [editMode, setEditMode] = useState(false);
  const [newTitle, setNewTitle] = useState(task.title);
  const [newDetails, setNewDetails] = useState(task.details || ""); // HTML möglich
  const [assigneeQuery, setAssigneeQuery] = useState(initialName);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState(
    task.assignee || ""
  );
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [targetBoardId, setTargetBoardId] = useState(String(boardId));
  const boardMap = useMemo(() => {
    const m = new Map();
    (availableBoards || []).forEach((b) => {
      if (b && b.id != null) m.set(String(b.id), b);
    });
    return m;
  }, [availableBoards]);
  const activeBoard = boardMap.get(String(targetBoardId)) || boardMap.get(String(boardId));
  const statusOptions = useMemo(() => {
    const opts = Array.isArray(activeBoard?.statuses) && activeBoard.statuses.length
      ? activeBoard.statuses
      : ['offen'];
    return opts;
  }, [activeBoard]);

  const [newPriority, setNewPriority] = useState(
    Number.isInteger(task.priority) ? task.priority : 0
  );
  const [labelsInput, setLabelsInput] = useState(
    (task.labels || []).join(", ")
  );
  const [newStatus, setNewStatus] = useState(task.status || statusOptions[0] || "offen");

  // Vollbild
  const [open, setOpen] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlTaskId = params.get("task");
      return urlTaskId === String(task.id);
    } catch {
      return false;
    }
  });
  // Auf schmalen Bildschirmen: Details und Kommentare als Tabs
  const [modalTab, setModalTab] = useState("details");
  const openModal = (tab = "details") => {
    setModalTab(tab);
    setOpen(true);
  };

  // Neue Kommentare hervorheben
  const [hasNewComment, setHasNewComment] = useState(false);

  // Berechne, ob Task neue Kommentare hat basierend auf Timestamps
  useEffect(() => {
    if (!task.last_comment_at) {
      setHasNewComment(false);
      return;
    }

    const currentUser = getCurrentUserFromToken();
    const userId = currentUser.id ? String(currentUser.id) : null;
    
    if (!userId) {
      setHasNewComment(false);
      return;
    }

    // Hole viewed_by Timestamp für aktuellen User
    const viewedBy = task.viewed_by || {};
    const lastViewedStr = viewedBy[userId];
    const lastCommentTime = new Date(task.last_comment_at).getTime();
    const lastViewedTime = lastViewedStr ? new Date(lastViewedStr).getTime() : 0;

    // Rot wenn letzter Kommentar neuerer als letzte Ansicht
    setHasNewComment(lastCommentTime > lastViewedTime);
  }, [task.last_comment_at, task.viewed_by]);

  // State mit Props synchron halten, falls Task/Board gewechselt wird
  useEffect(() => {
    setNewTitle(task.title);
    setNewDetails(task.details || "");
    setAssigneeQuery(initialName);
    setSelectedAssigneeId(task.assignee || "");
    setNewPriority(Number.isInteger(task.priority) ? task.priority : 0);
    setLabelsInput((task.labels || []).join(", "));
    setTargetBoardId(String(boardId));
  }, [task, initialName, boardId]);

  useEffect(() => {
    const fallback = task.status || statusOptions[0] || "offen";
    setNewStatus((prev) => (statusOptions.includes(prev) ? prev : fallback));
  }, [statusOptions, task.status]);

  // Socket Listener für neue Kommentare bei dieser Task
  useEffect(() => {
    const socket = getSocket();
    const handleCommentCreated = (data) => {
      if (data?.taskId && String(data.taskId) === String(task.id)) {
        // Zähler hochziehen, damit die Badge aktuell bleibt
        setCommentCount((prev) => (Number.isFinite(prev) ? prev + 1 : 1));
        if (!open) {
          setHasNewComment(true);
        }
      }
    };

    socket.on("comment:created", handleCommentCreated);

    return () => {
      socket.off("comment:created", handleCommentCreated);
    };
  }, [task.id, open]);

  // Markiere Task als angesehen wenn Modal geöffnet wird
  useEffect(() => {
    if (open) {
      // Optimistic UI: Markierung sofort entfernen wenn Task geöffnet wird
      setHasNewComment(false);
      
      // Rufe Backend auf, um diese Task als angesehen zu markieren
      markTaskAsViewed(boardId, task.id).catch((err) => {
        console.error('Error marking task as viewed:', err);
      });
    }
  }, [open, boardId, task.id]);


  // Kommentare
  const [comments, setComments] = useState([]);
  const [commentHtml, setCommentHtml] = useState("");
  const [mentionIds, setMentionIds] = useState([]);
  const [editMentionIds, setEditMentionIds] = useState([]);

  // URL aktualisieren wenn Modal öffnet/schliesst
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (open) {
      params.set("task", task.id);
    } else {
      params.delete("task");
    }
    const newUrl = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
    window.history.replaceState(null, "", newUrl);
  }, [open, task.id]);

  const loadComments = useCallback(async () => {
    try {
      const data = await listTaskComments(boardId, task.id);
      const normalized = (data || []).map((c) => ({
        ...c,
        mentions: Array.isArray(c.mentions) ? c.mentions : [],
      }));
      setComments(normalized);
    } catch (e) {
      console.error(e);
    }
  }, [boardId, task.id]);

  // Kommentar-Zähler (ohne Backend-Änderung)
  const commentCountDerived =
    task?.comment_count != null ? Number(task.comment_count) : 0;
  const [commentCount, setCommentCount] = useState(commentCountDerived);

  const loadCommentCount = useCallback(async () => {
    try {
      const data = await listTaskComments(boardId, task.id);
      setCommentCount(Array.isArray(data) ? data.length : 0);
    } catch (e) {
      console.error(e);
    }
  }, [boardId, task.id]);

  const uploadCommentImages = async (files) => {
    // Bilder als Task-Anhänge hochladen und URLs in den Kommentar einfügen
    const atts = await uploadTaskAttachments(task.id, files);
    // uploadTaskAttachments gibt bei dir data.attachments zurück → enthält .url
    return (atts || []).map((a) => a.url).filter(Boolean);
  };

  const addComment = async () => {
    const text = (commentHtml || "").trim();
    if (!text) return;
    try {
      await addTaskComment(boardId, task.id, text, mentionIds);
      setCommentHtml("");
      setMentionIds([]);
      await loadComments();
      await loadCommentCount(); // Zähler aktualisieren
    } catch (e) {
      console.error(e);
      alert(
        e?.response?.data?.error || "Kommentar konnte nicht gespeichert werden."
      );
    }
  };

  const removeComment = async (id) => {
    try {
      await deleteTaskComment(id);
      await loadComments();
      await loadCommentCount(); // Zähler aktualisieren
    } catch (e) {
      console.error(e);
      alert(
        e?.response?.data?.error || "Kommentar konnte nicht gelöscht werden."
      );
    }
  };

  useEffect(() => {
    // beim Mount / Task-Wechsel die Anzahl laden
    loadCommentCount();
  }, [loadCommentCount]);

  useEffect(() => {
    if (open) loadComments();
  }, [open, loadComments]);

  const suggestions = boardUsers.filter((user) =>
    user.name.toLowerCase().includes(assigneeQuery.toLowerCase())
  );

  const parseLabels = useCallback(
    (raw) =>
      raw
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
    []
  );

  const handleAssigneeChange = (e) => {
    setAssigneeQuery(e.target.value);
    setSelectedAssigneeId("");
    setShowSuggestions(true);
  };
  const selectSuggestion = (user) => {
    setAssigneeQuery(user.name);
    setSelectedAssigneeId(user.id);
    setShowSuggestions(false);
  };

  // Speichern/Löschen
  const doSave = async () => {
    let assigneeIdToSave = selectedAssigneeId;
    if (
      !assigneeIdToSave &&
      assigneeQuery.trim() !== "" &&
      suggestions.length > 0
    ) {
      assigneeIdToSave = suggestions[0].id;
    }
    const payload = {
      title: newTitle,
      assignee: assigneeIdToSave || null,
      status: newStatus,
      details: newDetails ?? "",
      priority: Number.isInteger(newPriority) ? newPriority : 0,
      labels: parseLabels(labelsInput),
    };
    try {
      const isBoardChange = String(targetBoardId) !== String(boardId);
      if (isBoardChange) {
        const res = await axios.post(`/tasks/${task.id}/move`, {
          targetBoardId: Number(targetBoardId),
          status: newStatus,
        });
        onTaskMoved?.({ task: res.data, fromBoardId: boardId, toBoardId: Number(targetBoardId) });
      } else {
        const res = await axios.put(
          `/boards/${boardId}/tasks/${task.id}`,
          payload
        );
        onTaskUpdated(res.data);
      }
      setEditMode(false);
      setOpen(false);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.error || "Konnte Task nicht speichern.");
    }
  };

  const doDelete = async () => {
    try {
      await axios.delete(`/boards/${boardId}/tasks/${task.id}`);
      onTaskDeleted(task.id);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.error || "Konnte Task nicht löschen.");
    }
  };

  // Anhänge zählen (normale + externe)
  const attachmentCount =
    typeof task?.attachment_count === "number"
      ? task.attachment_count + (Array.isArray(task?.external_attachments) ? task.external_attachments.length : 0)
      : (Array.isArray(task?.attachments) ? task.attachments.length : 0) + 
        (Array.isArray(task?.external_attachments) ? task.external_attachments.length : 0);

  // ESC fürs Modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // DnD global deaktivieren, solange das Vollbild-Modal offen ist
  useEffect(() => {
    if (!open) return;
    document.body.classList.add("no-dnd"); // Klasse auf <body>
    return () => {
      document.body.classList.remove("no-dnd");
    };
  }, [open]);

  /* ====== NEU: Kommentar bearbeiten ====== */
  const [editCommentId, setEditCommentId] = useState(null);
  const [editCommentHtml, setEditCommentHtml] = useState("");
  const me = useMemo(() => getCurrentUserFromToken(), []);

  const startEditComment = (c) => {
    setEditCommentId(c.id);
    setEditCommentHtml(c.content || "");
    setEditMentionIds(Array.isArray(c.mentions) ? c.mentions : []);
  };
  const cancelEditComment = () => {
    setEditCommentId(null);
    setEditCommentHtml("");
    setEditMentionIds([]);
  };
  const saveEditComment = async () => {
    if (!editCommentId) return;
    const html = (editCommentHtml || "").trim();
    if (!html) return;
    try {
      await updateTaskComment(editCommentId, html, editMentionIds);
      await loadComments();
      await loadCommentCount();
      setEditCommentId(null);
      setEditCommentHtml("");
      setEditMentionIds([]);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || "Kommentar konnte nicht aktualisiert werden.");
    }
  };

  const addMentionUser = (u) => {
    if (!u) return;
    const userId = String(u.id); // Normalize to string
    setMentionIds((prev) =>
      prev.some(id => String(id) === userId) ? prev : [...prev, userId]
    );
  };

  const addEditMentionUser = (u) => {
    if (!u) return;
    const userId = String(u.id); // Normalize to string
    setEditMentionIds((prev) =>
      prev.some(id => String(id) === userId) ? prev : [...prev, userId]
    );
  };

  /* --- Render --- */
  const assigneeUser = boardUsers.find((u) => u.id === task.assignee);

  return (
    <>
      <div
        className={`task-item ${hasNewComment ? "task-item-new-comment" : ""} ${editMode ? "is-editing" : ""}`}
        {...dragHandleProps}
      >
        {/* Expand (öffnet echtes Vollbild) */}
        <button
          aria-label="Task expandieren"
          title="Task expandieren"
          onClick={(e) => {
            e.stopPropagation();
            openModal("details");
          }}
          className="task-expand-btn"
        >
          <FaExpandAlt />
        </button>

        {editMode ? (
          <div>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Neuer Titel"
              className="task-edit-input"
            />
            {/* Inline weiterhin Plain-Textarea (keine Überraschung im Card-UI) */}
            <textarea
              value={stripHtml(newDetails)}
              onChange={(e) => setNewDetails(e.target.value)}
              placeholder="Beschreibung"
              className="task-edit-input"
              rows={3}
            />
            {/* Priority */}
            <label className="task-edit-label">Priorität</label>
            <select
              value={newPriority}
              onChange={(e) => setNewPriority(parseInt(e.target.value, 10))}
              className="task-edit-input"
            >
              <option value={0}>Low</option>
              <option value={1}>Medium</option>
              <option value={2}>High</option>
              <option value={3}>Highest</option>
            </select>

            {/* Labels */}
            <label className="task-edit-label">Labels (kommagetrennt)</label>
            <input
              type="text"
              value={labelsInput}
              onChange={(e) => setLabelsInput(e.target.value)}
              placeholder="z.B. backend, bug"
              className="task-edit-input"
            />

            {/* Assignee Autocomplete */}
            <label className="task-edit-label">Bearbeiter</label>
            <div className="autocomplete-container">
              <input
                type="text"
                value={assigneeQuery}
                onChange={handleAssigneeChange}
                placeholder="Bearbeiter eingeben..."
                className="task-edit-input"
                onFocus={() => setShowSuggestions(true)}
              />
              {showSuggestions && assigneeQuery && suggestions.length > 0 && (
                <ul className="autocomplete-suggestions">
                  {suggestions.map((user) => (
                    <li
                      key={user.id}
                      onClick={() => selectSuggestion(user)}
                      className="autocomplete-suggestion"
                    >
                      {user.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="task-edit-buttons">
              <button className="btn" onClick={doSave} title="Speichern" aria-label="Speichern">
                <FaSave />
              </button>
              <button className="btn btn-secondary" onClick={() => setEditMode(false)}>
                Abbrechen
              </button>
              <button className="btn btn-danger-ghost" onClick={doDelete} title="Löschen" aria-label="Löschen">
                <FaTrash />
              </button>
            </div>
          </div>
        ) : (
          <div onClick={() => setEditMode(true)}>
            <div className="task-card-top">
              <span className="task-id">#{task.id}</span>
              <span className="task-status">{task.status}</span>
            </div>
            <strong className="task-title">{task.title}</strong>

            <div className="task-meta">
              <span className={`priority-badge prio-${task.priority ?? 0}`}>
                {PRIO_TEXT[task.priority ?? 0]}
              </span>
              {(task.labels || []).map((lbl) => (
                <span key={lbl} className="label-badge">
                  {lbl}
                </span>
              ))}
            </div>

            {task.details && (
              <p className="task-details">
                {stripHtml(task.details).slice(0, 220)}
                {stripHtml(task.details).length > 220 ? "…" : ""}
              </p>
            )}

            <div className="task-card-footer">
              <div
                className={`assignee ${assigneeUser ? "" : "is-empty"}`}
                title={`Bearbeiter: ${assigneeUser?.name || "—"}`}
              >
                {assigneeUser ? (
                  <>
                    <span
                      aria-hidden
                      className="avatar avatar-xs"
                      style={{ background: getAvatarColor(assigneeUser.id) }}
                    >
                      {getInitials(assigneeUser.name)}
                    </span>
                    <span className="assignee-name">{assigneeUser.name}</span>
                  </>
                ) : (
                  <span className="assignee-name">Nicht zugewiesen</span>
                )}
              </div>

              {(attachmentCount > 0 || commentCount > 0) && (
                <div className="task-badges">
                  {attachmentCount > 0 && (
                    <div
                      className="task-attachments-inline"
                      title={`${attachmentCount} Anhang${
                        attachmentCount > 1 ? "e" : ""
                      }`}
                    >
                      <FaPaperclip />
                      <span className="count">{attachmentCount}</span>
                    </div>
                  )}

                  {commentCount > 0 && (
                    <div
                      className="task-comments-inline"
                      title={`${commentCount} Kommentar${
                        commentCount === 1 ? "" : "e"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal("comments");
                      }}
                    >
                      <FaCommentDots />
                      <span className="count">{commentCount}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ---------- ECHTES Vollbild-Modal (per Portal direkt im body) ---------- */}
      {open && createPortal(
        <div className="task-modal-backdrop" onClick={() => setOpen(false)}>
          <div
            className="task-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Task #${task.id} bearbeiten`}
            onClick={(e) => e.stopPropagation()}
            draggable={false} /* <- nicht draggable */
          >
            <div className="task-modal-head" draggable={false}>
              <div className="task-modal-heading">
                <span className="task-id">#{task.id}</span>
                <h3>Task bearbeiten</h3>
              </div>
              <button
                className="icon-btn task-modal-close"
                onClick={() => setOpen(false)}
                aria-label="Schließen"
              >
                <FaTimes />
              </button>
            </div>

            {/* Tabs – nur auf schmalen Bildschirmen sichtbar */}
            <div className="task-modal-tabs" role="tablist">
              <button
                role="tab"
                aria-selected={modalTab === "details"}
                className={`task-modal-tab ${modalTab === "details" ? "is-active" : ""}`}
                onClick={() => setModalTab("details")}
              >
                Details
              </button>
              <button
                role="tab"
                aria-selected={modalTab === "comments"}
                className={`task-modal-tab ${modalTab === "comments" ? "is-active" : ""}`}
                onClick={() => setModalTab("comments")}
              >
                Kommentare
                {comments.length > 0 && <span className="count">{comments.length}</span>}
              </button>
            </div>

            <div className={`task-modal-body tab-${modalTab}`}>
              {/* linke Seite: Editor + Metadaten */}
              <div className="task-modal-left">
                <div className="task-detail-block">
                  <label className="task-detail-label">Titel</label>
                  <input
                    type="text"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    className="task-edit-input"
                  />
                </div>

                <div className="task-detail-block">
                  <label className="task-detail-label">Beschreibung</label>
                  <RichTextEditor value={newDetails} onChange={setNewDetails} />
                </div>

                <div className="task-detail-block">
                  <label className="task-detail-label">Board</label>
                  <select
                    className="task-edit-input"
                    value={targetBoardId}
                    onChange={(e) => {
                      const nextId = e.target.value;
                      setTargetBoardId(nextId);
                      const nextStatuses = (() => {
                        const b = boardMap.get(String(nextId));
                        return Array.isArray(b?.statuses) && b.statuses.length ? b.statuses : ['offen'];
                      })();
                      setNewStatus((prev) => (nextStatuses.includes(prev) ? prev : nextStatuses[0] || 'offen'));
                    }}
                  >
                    {(availableBoards || []).length === 0 && (
                      <option value={targetBoardId}>Aktuelles Board</option>
                    )}
                    {(availableBoards || []).map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                  <div className="task-detail-hint">
                    Nur Boards, in denen du Mitglied bist.
                  </div>
                </div>

                <div className="task-grid-2">
                  <div className="task-detail-block">
                    <label className="task-detail-label">Status</label>
                    <select
                      className="task-edit-input"
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                    >
                      {statusOptions.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="task-detail-block">
                    <label className="task-detail-label">Priorität</label>
                    <select
                      value={newPriority}
                      onChange={(e) =>
                        setNewPriority(parseInt(e.target.value, 10))
                      }
                      className="task-edit-input"
                    >
                      <option value={0}>Low</option>
                      <option value={1}>Medium</option>
                      <option value={2}>High</option>
                      <option value={3}>Highest</option>
                    </select>
                  </div>

                  <div className="task-detail-block">
                    <label className="task-detail-label">Bearbeiter</label>
                    <select
                      className="task-edit-input"
                      value={selectedAssigneeId || ""}
                      onChange={(e) => {
                        const val = e.target.value ? Number(e.target.value) : "";
                        setSelectedAssigneeId(val);
                        if (!val) {
                          setAssigneeQuery("");
                          setShowSuggestions(false);
                        } else {
                          const found = boardUsers.find((u) => u.id === val);
                          if (found) setAssigneeQuery(found.name);
                        }
                      }}
                    >
                      <option value="">— keiner —</option>
                      {boardUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                    <div className="autocomplete-container">
                      <input
                        type="text"
                        value={assigneeQuery}
                        onChange={(e) => setAssigneeQuery(e.target.value)}
                        placeholder="Namen tippen…"
                        className="task-edit-input"
                        onFocus={() => setShowSuggestions(true)}
                      />
                      {showSuggestions &&
                        assigneeQuery &&
                        suggestions.length > 0 && (
                          <ul className="autocomplete-suggestions">
                            {suggestions.map((user) => (
                              <li
                                key={user.id}
                                onClick={() => {
                                  setAssigneeQuery(user.name);
                                  setSelectedAssigneeId(user.id);
                                  setShowSuggestions(false);
                                }}
                                className="autocomplete-suggestion"
                              >
                                {user.name}
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>
                  </div>

                  <div className="task-detail-block">
                    <label className="task-detail-label">Ersteller</label>
                    <div className="task-readonly">
                      {boardUsers.find((u) => u.id === task.author)?.name || "Unbekannt"}
                    </div>
                  </div>

                  <div className="task-detail-block">
                    <label className="task-detail-label">Erstellt am</label>
                    <div className="task-readonly">
                      {task.created_at ? new Date(task.created_at).toLocaleString("de-DE") : "—"}
                    </div>
                  </div>
                </div>
                <div className="task-detail-block">
                  <label className="task-detail-label">
                    Labels (kommagetrennt)
                  </label>
                  <input
                    type="text"
                    value={labelsInput}
                    onChange={(e) => setLabelsInput(e.target.value)}
                    placeholder="z.B. backend, bug"
                    className="task-edit-input"
                  />
                </div>

                {/* Attachments */}
                <AttachmentsPanel
                  taskId={task.id}
                  externalItems={task.external_attachments || []}
                />
              </div>

              {/* rechte Seite: Kommentare */}
              <div className="task-modal-right">
                <div className="comments-panel">
                  <div className="comments-panel-head">Kommentare</div>
                  <div className="comments-panel-list">
                    {comments.length === 0 ? (
                      <div className="comments-empty">
                        Noch keine Kommentare.
                      </div>
                    ) : (
                      comments.map((c) => {
                        // ROBUSTER Autor-Check: ID -> E-Mail -> Name
                        const isAuthor = (() => {
                          const meId = me.id != null ? String(me.id) : null;
                          const idCandidates = [
                            c.author_id,
                            c.user_id,
                            c.authorId,
                            c.uid,
                            c.userId,
                            c.created_by,
                          ].map(v => (v != null ? String(v) : null));
                          if (meId && idCandidates.some(v => v && v === meId)) return true;

                          const meMail = me.email ? String(me.email).toLowerCase() : null;
                          const mailCandidates = [
                            c.author_email,
                            c.email,
                            c.user_email,
                          ].map(v => (v ? String(v).toLowerCase() : null));
                          if (meMail && mailCandidates.some(v => v && v === meMail)) return true;

                          const meName = me.name ? String(me.name).toLowerCase() : null;
                          const nameCandidates = [
                            c.author_name,
                            c.username,
                            c.user_name,
                            c.name,
                          ].map(v => (v ? String(v).toLowerCase() : null));
                          if (meName && nameCandidates.some(v => v && v === meName)) return true;

                          return false;
                        })();

                        const isEditing = editCommentId === c.id;

                        return (
                          <div key={c.id} className="comment-item">
                            <div className="comment-meta">
                              <span className="comment-author">{c.author_name ?? "Unbekannt"}</span>
                              <span>{new Date(c.created_at).toLocaleString()}</span>
                            </div>

                            {!isEditing ? (
                              <div
                                className="comment-text"
                                dangerouslySetInnerHTML={{ __html: c.content }}
                              />
                            ) : (
                              <div className="comment-edit-block">
                                <RichTextEditor
                                  mode="comment"
                                  value={editCommentHtml}
                                  onChange={setEditCommentHtml}
                                  placeholder="Kommentar bearbeiten…"
                                  onUploadImages={uploadCommentImages}
                                  mentionUsers={boardUsers}
                                  mentionExcludeIds={editMentionIds}
                                  onMentionAdd={addEditMentionUser}
                                />
                              </div>
                            )}

                            <div className="comment-actions">
                              {/* Bearbeiten links vom Löschen, nur für Autor */}
                              {isAuthor && !isEditing && (
                                <button onClick={() => startEditComment(c)}>
                                  Bearbeiten
                                </button>
                              )}
                              {isEditing ? (
                                <>
                                  <button onClick={saveEditComment}>
                                    Speichern
                                  </button>
                                  <button onClick={cancelEditComment}>
                                    Abbrechen
                                  </button>
                                </>
                              ) : (
                                <button className="is-danger" onClick={() => removeComment(c.id)}>
                                  Löschen
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="comments-panel-input">
                    <RichTextEditor
                      mode="comment"
                      value={commentHtml}
                      onChange={setCommentHtml}
                      placeholder="Kommentar schreiben…"
                      onUploadImages={uploadCommentImages}
                      mentionUsers={boardUsers}
                      mentionExcludeIds={mentionIds}
                      onMentionAdd={addMentionUser}
                    />
                    <button className="btn comment-send" onClick={addComment}>Senden</button>
                  </div>
                </div>
              </div>
            </div>
            {/* body */}

            {/* STICKY FOOTER – bleibt immer sichtbar */}
            <div className="task-modal-footer" draggable={false}>
              <button onClick={doSave} className="btn">
                <FaSave /> Speichern
              </button>
              <button onClick={() => setOpen(false)} className="btn btn-secondary">
                Abbrechen
              </button>
              <button onClick={doDelete} className="btn btn-danger-ghost footer-delete" aria-label="Löschen">
                <FaTrash /> <span className="footer-delete-label">Löschen</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default TaskItem;
